
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  GoogleMap,
  LoadScript,
  Marker,
  InfoWindow,
  StandaloneSearchBox,
} from "@react-google-maps/api";
import styles from "./style.module.css";
import ClockIcon from 'assets/SVG/ClockIcon';
import PhoneSvg from "../../assets/SVG/PhoneSvg";

const defaultCenter = {
  lat: 40.150011,
  lng: 44.495715,
};

const libraries = ["places"];

const Map = React.memo(
  ({
    addresses = [],
    mapCenter,
    setMapCenter,
    selectedIndex,
    setSelected,
    selected,
    handleMarkerClick,
    disableMap,
    input,
    setAddressName,
    fitAllMarkers = false,
  }) => {

    const searchBoxRef = useRef();
    const mapRef = useRef(null);
    const fittedKeyRef = useRef("");
    const { currentLanguage } = useSelector(({ main }) => main);
    const [markerPosition, setMarkerPosition] = useState();
    const [countAddress, setCountAddress] = useState(0);
    // Freeze center for fitAllMarkers so React doesn't fight fitBounds / drag
    const [frozenCenter] = useState(() => ({
      lat: parseFloat(mapCenter?.lat) || defaultCenter.lat,
      lng: parseFloat(mapCenter?.lng) || defaultCenter.lng,
    }));

    const mapContainerStyle = {
      display: disableMap ? "none" : "block",
      width: "100%",
      height: "100%",
      outline: "none",
    };

    const validMapCenter = {
      lat: parseFloat(mapCenter?.lat) || defaultCenter.lat,
      lng: parseFloat(mapCenter?.lng) || defaultCenter.lng,
    };

    const addressesKey = (addresses || [])
      .map((a) => `${a?.id}:${a?.location?.lat},${a?.location?.lng}`)
      .join("|");

    const fitMapToAddresses = useCallback((mapInstance, force = false) => {
      const map = mapInstance || mapRef.current;
      if (!fitAllMarkers || !map || !window.google?.maps || !addresses?.length) {
        return;
      }

      if (!force && fittedKeyRef.current === addressesKey) {
        return;
      }

      const bounds = new window.google.maps.LatLngBounds();
      let hasPoint = false;

      addresses.forEach((address) => {
        const lat = parseFloat(address?.location?.lat);
        const lng = parseFloat(address?.location?.lng);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          bounds.extend({ lat, lng });
          hasPoint = true;
        }
      });

      if (!hasPoint) return;

      fittedKeyRef.current = addressesKey;
      map.fitBounds(bounds, 72);

      window.google.maps.event.addListenerOnce(map, "idle", () => {
        const zoom = map.getZoom();
        if (typeof zoom === "number" && zoom > 13) {
          map.setZoom(13);
        }
      });
    }, [addresses, addressesKey, fitAllMarkers]);

    const getAddressFromCoordinates = async (markerPosition) => {
      try {
        const positions = {
          lat:
            typeof markerPosition?.lat == "function"
              ? markerPosition?.lat()
              : markerPosition?.lat,
          lng:
            typeof markerPosition?.lng == "function"
              ? markerPosition?.lng()
              : markerPosition?.lng,
        };
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${positions.lat},${positions.lng}&key=AIzaSyCPnJK15brSbMXRXL-uhdtyb0ZKNizR_0I`
        );
        const data = await response.json();
        if (data.status === "OK") {
          !!countAddress && setAddressName(data.results[0].formatted_address);
          setCountAddress((prev) => prev + 1);
        }
        else {
          console.error("Error fetching address:", data);
        }
      }
      catch (error) {
        console.error("Error fetching address:", error);
      }
    };

    const onPlacesChanged = () => {
      const places = searchBoxRef.current.state.searchBox?.getPlaces();
      if (places.length > 0) {
        const { geometry } = places[0];
        const { location } = geometry;
        const latLng = { lat: location.lat(), lng: location.lng() };
        if (!disableMap) {
          setMapCenter(latLng);
          setMarkerPosition(latLng);
        }
        else {
          getAddressFromCoordinates(latLng);
        }
      }
    };

    const handleMarkerDragEnd = (mapProps) => {
      setMarkerPosition(mapProps.latLng);
    };

    const handleClick = (mapProps) => {
      setMarkerPosition(mapProps.latLng);
    };

    useLayoutEffect(() => {
      if (fitAllMarkers) return;
      if (!Object.keys(markerPosition || {}).length)
        setTimeout(() => {
          setMarkerPosition(defaultCenter);
        }, 1000);
      if (Object.keys(markerPosition || {})?.length)
          getAddressFromCoordinates(markerPosition);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markerPosition?.lat, fitAllMarkers]);

    useEffect(() => {
      if (fitAllMarkers) {
        fitMapToAddresses();
      }
    }, [addressesKey, fitAllMarkers, fitMapToAddresses]);

    useEffect(() => {
      if (!fitAllMarkers || !mapRef.current || selectedIndex == null) return;
      const address = addresses?.[selectedIndex];
      const lat = parseFloat(address?.location?.lat);
      const lng = parseFloat(address?.location?.lng);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return;
      // Only gently center selected branch; keep current zoom so markers stay synced
      mapRef.current.panTo({ lat, lng });
    }, [selectedIndex, addresses, fitAllMarkers]);

    return (
      <div
        className={styles.mapShell}
        style = {{
          height: disableMap ? "auto" : "460px",
          width: "100%",
        }}
      >
        <LoadScript
          googleMapsApiKey = { process.env.REACT_APP_GOOGLE_MAP }
          libraries = { libraries }
        >
          {disableMap ? (
            <StandaloneSearchBox
              ref = { searchBoxRef }
              onPlacesChanged = { onPlacesChanged }
            >
              <div>{ input }</div>
            </StandaloneSearchBox>
          ) : (
            <>
              <StandaloneSearchBox
                ref = { searchBoxRef }
                onPlacesChanged = { onPlacesChanged }
              >
                <div>{ input }</div>
              </StandaloneSearchBox>
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                mapContainerClassName={styles.mapCanvas}
                center={fitAllMarkers ? frozenCenter : validMapCenter}
                zoom={fitAllMarkers ? 11 : 14}
                onLoad={(map) => {
                  mapRef.current = map;
                  if (fitAllMarkers) {
                    fittedKeyRef.current = "";
                    fitMapToAddresses(map, true);
                  }
                  const div = map.getDiv?.();
                  if (div) {
                    div.setAttribute("tabindex", "-1");
                    div.style.outline = "none";
                  }
                }}
                onClick={fitAllMarkers ? (e) => {
                  const div = mapRef.current?.getDiv?.();
                  div?.blur?.();
                  e?.domEvent?.target?.blur?.();
                } : handleClick}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: true,
                  gestureHandling: "greedy",
                  clickableIcons: false,
                  keyboardShortcuts: false,
                }}
              >
                {addresses?.length > 0 &&
                  addresses.map((address, index) => (
                    <Marker
                      key={address.id || index}
                      position={{
                        lat:
                          parseFloat(address.location?.lat) ||
                          defaultCenter.lat,
                        lng:
                          parseFloat(address.location?.lng) ||
                          defaultCenter.lng,
                      }}
                      icon={require("./assets/mapMarker.png")}
                      onClick={() => handleMarkerClick(address, index)}
                    />
                  ))
                }
                {!fitAllMarkers && markerPosition && (
                  <Marker
                    position = { markerPosition }
                    draggable = { true }
                    onDragEnd = { handleMarkerDragEnd }
                    icon = { require("./assets/mapMarker.png") }
                  />
                )}
                { selected && selectedIndex !== null ? (
                  <InfoWindow
                    position = { selected }
                    onCloseClick = { () => setSelected(null) }
                  >
                    <div className = { styles.addr_cont_inf }>
                      <span className = { styles.locname_ch }>
                        { addresses[selectedIndex]?.["address_" + currentLanguage] }
                      </span>
                      <img
                        src = { process.env.REACT_APP_STORAGE_URL + addresses[selectedIndex]?.["image_" + currentLanguage] }
                        alt = { addresses[selectedIndex]?.["image_" + currentLanguage] }
                        style = {{ maxHeight: 168, borderRadius: '15px' }}
                        className={styles.branchImage}
                      />
                        <div className={styles.time_phone}>
                          <div className={styles.chosen_phone_mail_user}>
                            <ClockIcon/>
                            { addresses[selectedIndex]?.["work_time_" + currentLanguage] }
                          </div>
                            {
                              window.innerWidth > 768 ?
                                <div
                                  style = {{
                                    width: '1px',
                                    height: '10px',
                                    backgroundColor: 'gray'
                                  }}
                                />
                                :
                                null
                            }
                          <a
                            href = "tel:374 10-44-99-68"
                            className = { styles.chosen_phone_mail_user }
                            style = {{ color: 'black' }}
                          >
                          <PhoneSvg
                            width = { 12 }
                            height = { 12 }
                          />
                          { addresses[selectedIndex]?.["tel_" + currentLanguage] }
                        </a>
                      </div>
                    </div>
                  </InfoWindow>
                )
                :
                null
                }
              </GoogleMap>
            </>
          )}
        </LoadScript>
      </div>
    );
  }
);

export default Map;
